/**
 * Modal de promoção no add-to-cart — sitewide.
 *
 * Fluxo: o cliente adiciona qualquer item → em vez da sacola abre uma modal
 * com um convite e um botão que leva pra página da promoção → ele clica no
 * botão (navega) OU fecha a modal → aí a sacola abre.
 *
 * É o mesmo mecanismo do `conso-lp-brinde.js`, só que sem produto dentro: a
 * modal aqui é um convite, não uma oferta pra aceitar. Ver lá o porquê da
 * guarda em `ThemeDrawer.open` (na 4.x é o `<theme-drawer>` quem abre a
 * sacola; a flag global antiga não segura mais nada).
 *
 * ONDE NÃO ABRE
 * - Página com a própria modal de brinde (`script[data-conso-lp-brinde]`): a
 *   LP e a PDP do costume. Lá o add já abre a modal do brinde.
 * - Item adicionado é variante de um produto excluído (o costume).
 * - Carrinho já tem o brinde da promo (`_conso_brinde_lp`).
 * - Frequência "uma vez por visita" e a modal já apareceu nesta sessão.
 * - (opção, ligada por padrão) O carrinho tem mais de 1 unidade depois do
 *   add: o convite é pra quem acabou de começar a compra, com um item só.
 *
 * DUAS ETAPAS
 * A modal tem a oferta (tarja de alerta, título, card do brinde com "de R$ X
 * por R$ 0", botão) e uma confirmação ("Tem certeza que não quer?"). Qualquer
 * tentativa de sair da oferta — X, link de escape, clique fora, Esc — troca
 * pra confirmação em vez de fechar. Da confirmação, o botão principal vai pra
 * promoção e o escape fecha de verdade (e aí a sacola abre).
 */
(() => {
  if (window.__consoPromoModal) return;
  window.__consoPromoModal = true;

  const SEL_DADOS = 'script[type="application/json"][data-conso-promo-modal]';
  const SEL_PAGINA_COM_BRINDE = 'script[data-conso-lp-brinde]';
  const SEL_ATC = 'button[type="submit"], .add-to-cart-button, .sticky-add-to-cart__button';
  // Só cliques que de fato são "adicionar ao carrinho": o formulário de compra
  // e a barra sticky. Um submit qualquer (busca, newsletter) não arma nada.
  const SEL_FORM = 'product-form-component, sticky-add-to-cart';
  const EVENTO_ADD = 'shopify:cart:lines-update';
  const PROP_BRINDE = '_conso_brinde_lp';
  const MARCA_GUARDA = '__consoPromoModalGuarded';
  const CHAVE_SESSAO = 'consoPromoModalVisto';
  const TETO_ESPERA_MS = 8000;

  const rotas = () => (window.Theme && window.Theme.routes) || {};

  let dados = null;
  let pendente = false;
  let expiracao = null;
  let segurando = false;
  let modal = null;
  let indoComprar = false;

  /* ------------------------------------------------------------------ dados */

  function lerDados() {
    if (dados) return dados;

    const el = document.querySelector(SEL_DADOS);
    if (!el) return null;

    try {
      dados = JSON.parse(el.textContent);
    } catch (e) {
      console.error('[conso-promo-modal] JSON inválido na section', e);
      dados = null;
    }

    return dados;
  }

  /** A página já tem modal própria de brinde: esta não entra. */
  const paginaComBrinde = () => Boolean(document.querySelector(SEL_PAGINA_COM_BRINDE));

  /** `merchandiseId` vem como gid ou como número; compara pelo final. */
  const numeroDe = (id) => String(id || '').replace(/\D+$/, '').split('/').pop();

  /** O item adicionado é uma camisa gatilho? Aí vale o conjunto "camisa". */
  function algumaCamisa(linhas, d) {
    const camisas = new Set((d.variantesCamisa || []).map(String));
    if (camisas.size === 0) return false;

    return (linhas || []).some((linha) => camisas.has(numeroDe(linha && linha.merchandiseId)));
  }

  /** Copy e link da modal: o conjunto da camisa ou o padrão. */
  const conjuntoPara = (linhas, d) =>
    algumaCamisa(linhas, d) && d.camisa && d.camisa.titulo ? d.camisa : d;

  function algumExcluido(linhas, d) {
    const excluidas = new Set((d.variantesExcluidas || []).map(String));
    if (excluidas.size === 0) return false;

    return (linhas || []).some((linha) => excluidas.has(numeroDe(linha && linha.merchandiseId)));
  }

  function jaViu(d) {
    if (d.frequencia !== 'sessao') return false;

    try {
      return sessionStorage.getItem(CHAVE_SESSAO) === '1';
    } catch (e) {
      return false;
    }
  }

  function marcarVisto() {
    try {
      sessionStorage.setItem(CHAVE_SESSAO, '1');
    } catch (e) {
      /* modo privado sem storage: mostra de novo, sem erro */
    }
  }

  /* ------------------------------------------------------- guarda do drawer */

  function armarGuarda() {
    const ThemeDrawer = window.customElements && customElements.get('theme-drawer');
    if (!ThemeDrawer || ThemeDrawer.prototype[MARCA_GUARDA]) return;
    ThemeDrawer.prototype[MARCA_GUARDA] = true;

    const original = ThemeDrawer.prototype.open;
    if (typeof original !== 'function') return;

    ThemeDrawer.prototype.open = function () {
      if (segurando && this.querySelector('cart-drawer-component')) return undefined;
      return original.apply(this, arguments);
    };
  }

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('theme-drawer').then(armarGuarda);
  }

  function liberar() {
    segurando = false;
  }

  function desarmar() {
    pendente = false;
    clearTimeout(expiracao);
    liberar();
  }

  /* --------------------------------------------------------------- carrinho */

  async function carrinhoAtual() {
    const r = await fetch(`${rotas().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    return r.json();
  }

  const temOBrinde = (carrinho) =>
    Boolean(
      carrinho &&
        Array.isArray(carrinho.items) &&
        carrinho.items.some((i) => i && i.properties && i.properties[PROP_BRINDE])
    );

  function abrirSacola() {
    liberar();

    const drawer = document.querySelector('cart-drawer-component');
    const theme = drawer && drawer.closest('theme-drawer');
    if (theme && typeof theme.open === 'function') {
      theme.open();
      return;
    }
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    const gatilho = document.querySelector('[data-testid="cart-drawer-trigger"]');
    if (gatilho) gatilho.click();
  }

  /* --------------------------------------------------------------- tracking */

  // Nome próprio (`promo_modal_*`), pela mesma regra dos `brinde_*`: nome
  // padrão do GA4 registrado como definição personalizada fica ambíguo.
  function rastrear(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  /* ------------------------------------------------------------------ modal */

  let etapa = 'oferta';

  function montarModal(d) {
    if (modal) return modal;

    modal = document.createElement('dialog');
    modal.className = 'conso-promo-modal';
    modal.setAttribute('aria-labelledby', 'conso-promo-modal-titulo');

    const caixa = document.createElement('div');
    caixa.className = 'conso-promo-modal__caixa';

    const fechar = document.createElement('button');
    fechar.type = 'button';
    fechar.className = 'conso-promo-modal__x';
    fechar.setAttribute('aria-label', 'Fechar');
    fechar.innerHTML =
      '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15"/></svg>';
    fechar.addEventListener('click', () => tentarSair());
    caixa.appendChild(fechar);

    /* ---- etapa 1: a oferta ---- */
    const oferta = document.createElement('div');
    oferta.className = 'conso-promo-modal__etapa conso-promo-modal__etapa--oferta';

    const alerta = document.createElement('p');
    alerta.className = 'conso-promo-modal__alerta';
    alerta.innerHTML =
      '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 3.2 2.6 16h14.8L10 3.2Z"/><path d="M10 8v3.6M10 14h.01"/></svg><span></span>';
    alerta.hidden = true;
    oferta.appendChild(alerta);

    const titulo = document.createElement('h2');
    titulo.className = 'conso-promo-modal__titulo';
    titulo.id = 'conso-promo-modal-titulo';
    oferta.appendChild(titulo);

    // HTML de propósito: o setting é richtext (vem do editor do tema, com
    // <p> e <strong>), e o operador quer destacar palavras como "brinde".
    const texto = document.createElement('div');
    texto.className = 'conso-promo-modal__texto';
    texto.hidden = true;
    oferta.appendChild(texto);

    // Card do brinde: foto, nome e "de R$ X por R$ 0". É o argumento de venda.
    if (d.brinde) {
      const card = document.createElement('div');
      card.className = 'conso-promo-modal__brinde';

      if (d.imagem) {
        const figura = document.createElement('div');
        figura.className = 'conso-promo-modal__brinde-foto';
        const img = document.createElement('img');
        img.src = d.imagem;
        img.alt = d.imagemAlt || d.brinde.titulo || '';
        img.loading = 'lazy';
        figura.appendChild(img);
        card.appendChild(figura);
      }

      const info = document.createElement('div');
      info.className = 'conso-promo-modal__brinde-info';

      const nome = document.createElement('p');
      nome.className = 'conso-promo-modal__brinde-nome';
      nome.textContent = d.brinde.titulo || '';
      info.appendChild(nome);

      const precos = document.createElement('p');
      precos.className = 'conso-promo-modal__brinde-precos';
      const de = document.createElement('s');
      de.className = 'conso-promo-modal__brinde-de';
      de.textContent = d.brinde.preco || '';
      const por = document.createElement('strong');
      por.className = 'conso-promo-modal__brinde-por';
      por.textContent = 'R$ 0,00';
      precos.append(de, ' ', por);
      info.appendChild(precos);

      const selo = document.createElement('span');
      selo.className = 'conso-promo-modal__brinde-selo';
      selo.textContent = d.brinde.rotuloGratis || 'Grátis';
      info.appendChild(selo);

      card.appendChild(info);
      oferta.appendChild(card);
    }

    const acoes = document.createElement('div');
    acoes.className = 'conso-promo-modal__acoes';

    const ir = document.createElement('a');
    ir.className = 'conso-promo-modal__ir button';
    // Único caminho que NÃO abre a sacola: o cliente está indo pra promoção, e
    // empurrar a sacola por cima da navegação só atrapalharia.
    ir.addEventListener('click', () => {
      indoComprar = true;
      rastrear({ event: 'promo_modal_click', promo_modal_destino: ir.href, promo_modal_etapa: 'oferta' });
    });

    const seguir = document.createElement('button');
    seguir.type = 'button';
    seguir.className = 'conso-promo-modal__seguir';
    seguir.textContent = d.fechar || 'Continuar para o carrinho';
    seguir.addEventListener('click', () => tentarSair());

    acoes.append(ir, seguir);
    oferta.appendChild(acoes);

    /* ---- etapa 2: tem certeza? ---- */
    const c = d.confirmar || {};
    const confirmar = document.createElement('div');
    confirmar.className = 'conso-promo-modal__etapa conso-promo-modal__etapa--confirmar';
    confirmar.hidden = true;

    const cIcone = document.createElement('div');
    cIcone.className = 'conso-promo-modal__confirmar-icone';
    cIcone.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 12v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8"/><path d="M2 7h20v5H2z"/><path d="M12 7v14"/><path d="M12 7c-1.5-3.5-6-3.5-6-1 0 1.5 3 1.2 6 1Zm0 0c1.5-3.5 6-3.5 6-1 0 1.5-3 1.2-6 1Z"/></svg>';
    confirmar.appendChild(cIcone);

    const cTitulo = document.createElement('h2');
    cTitulo.className = 'conso-promo-modal__titulo';
    cTitulo.textContent = c.titulo || 'Tem certeza que não quer?';
    confirmar.appendChild(cTitulo);

    const cTexto = document.createElement('div');
    cTexto.className = 'conso-promo-modal__texto';
    cTexto.innerHTML = c.texto || '';
    cTexto.hidden = !c.texto;
    confirmar.appendChild(cTexto);

    const cAcoes = document.createElement('div');
    cAcoes.className = 'conso-promo-modal__acoes';

    const cIr = document.createElement('a');
    cIr.className = 'conso-promo-modal__ir button';
    cIr.textContent = c.botao || 'Sim, quero a polo grátis';
    cIr.addEventListener('click', () => {
      indoComprar = true;
      rastrear({ event: 'promo_modal_click', promo_modal_destino: cIr.href, promo_modal_etapa: 'confirmar' });
    });

    const cSair = document.createElement('button');
    cSair.type = 'button';
    cSair.className = 'conso-promo-modal__seguir';
    cSair.textContent = c.sair || 'Não, seguir para a sacola';
    cSair.addEventListener('click', () => modal.close());

    cAcoes.append(cIr, cSair);
    confirmar.appendChild(cAcoes);

    caixa.append(oferta, confirmar);
    modal.appendChild(caixa);

    function mostrarEtapa(nome) {
      etapa = nome;
      oferta.hidden = nome !== 'oferta';
      confirmar.hidden = nome !== 'confirmar';
      modal.classList.toggle('conso-promo-modal--confirmar', nome === 'confirmar');
      modal.setAttribute('aria-labelledby', nome === 'confirmar' ? 'conso-promo-modal-confirmar' : 'conso-promo-modal-titulo');
    }
    cTitulo.id = 'conso-promo-modal-confirmar';

    // Sair da oferta não fecha: pergunta antes. Da confirmação, fecha.
    function tentarSair() {
      if (etapa === 'oferta') {
        mostrarEtapa('confirmar');
        rastrear({ event: 'promo_modal_confirm_view' });
        return;
      }
      modal.close();
    }

    // O <dialog> ocupa a tela toda e a caixa é filha: clique fora é no backdrop.
    modal.addEventListener('click', (e) => {
      if (e.target === modal) tentarSair();
    });

    // Esc dispara `cancel` antes de fechar; na oferta vira a pergunta.
    modal.addEventListener('cancel', (e) => {
      if (etapa === 'oferta') {
        e.preventDefault();
        tentarSair();
      }
    });

    modal.addEventListener('close', () => {
      liberar();
      if (!indoComprar) {
        rastrear({ event: 'promo_modal_skip', promo_modal_etapa: etapa });
        abrirSacola();
      }
      indoComprar = false;
    });

    // Copy e link mudam conforme o item adicionado: a modal é uma só,
    // repintada a cada abertura.
    modal.__pintar = (conj) => {
      alerta.querySelector('span').textContent = d.alerta || '';
      alerta.hidden = !d.alerta;
      titulo.textContent = conj.titulo || '';
      texto.innerHTML = conj.texto || '';
      texto.hidden = !conj.texto;
      ir.textContent = conj.botao || 'Acessar promoção';
      ir.href = conj.url || '/';
      cIr.href = conj.url || '/';
      mostrarEtapa('oferta');
    };

    document.body.appendChild(modal);
    return modal;
  }

  function abrirModal(d, conjunto) {
    const el = montarModal(d);
    el.__pintar(conjunto || d);
    indoComprar = false;
    el.showModal();
    marcarVisto();
    rastrear({
      event: 'promo_modal_view',
      promo_modal_destino: (conjunto || d).url || '',
      promo_modal_tipo: conjunto && conjunto !== d ? 'camisa' : 'padrao',
    });

    // A guarda fica armada enquanto a modal está aberta. O cart-drawer tenta
    // abrir duas vezes (uma síncrona no add e outra no `.then` da promise,
    // que pode chegar depois da nossa leitura do carrinho); soltar cedo
    // deixava a sacola abrir por trás da modal. Quem solta é o `close`.
  }

  /* ---------------------------------------------------------- interceptação */

  document.addEventListener(
    'click',
    (evento) => {
      const alvo = evento.target instanceof Element ? evento.target.closest(SEL_ATC) : null;
      if (!alvo || alvo.disabled) return;
      if (!alvo.closest(SEL_FORM) && !alvo.classList.contains('add-to-cart-button')) return;

      const d = lerDados();
      if (!d || paginaComBrinde() || jaViu(d)) return;
      if (modal && modal.open) return;

      pendente = true;
      segurando = true;

      clearTimeout(expiracao);
      expiracao = setTimeout(desarmar, TETO_ESPERA_MS);
    },
    true
  );

  document.addEventListener(EVENTO_ADD, (evento) => {
    if (!pendente) return;

    // Só add do cliente. A ponte do `conso-cart-event-bridge` re-emite com
    // 'update', então nada que venha de script de fundo cai aqui.
    if (evento.action !== 'add') return;

    pendente = false;
    clearTimeout(expiracao);

    const d = lerDados();

    // Item excluído (o costume): devolve o drawer ao normal e sai. Tem que ser
    // ANTES de a promise resolver — é nela que a sacola tenta abrir.
    if (!d || algumExcluido(evento.lines, d)) {
      liberar();
      return;
    }

    const conjunto = conjuntoPara(evento.lines, d);

    const seguir = async () => {
      let carrinho = null;
      try {
        carrinho = await carrinhoAtual();
      } catch (e) {
        console.warn('[conso-promo-modal] não deu pra ler o carrinho', e);
      }

      // Já pegou o brinde da promo: o convite não faz sentido.
      if (temOBrinde(carrinho)) {
        abrirSacola();
        return;
      }

      // Só carrinho com 1 unidade: o cliente acabou de começar a compra. Quem
      // já tem mais coisa na sacola segue pra ela sem convite.
      if (d.soPrimeiroItem && (!carrinho || Number(carrinho.item_count) !== 1)) {
        abrirSacola();
        return;
      }

      abrirModal(d, conjunto);
    };

    const promessa = evento.promise;
    if (promessa && typeof promessa.then === 'function') {
      promessa
        .then((r) => {
          if (r && r.detail && r.detail.didError) {
            liberar();
            return;
          }
          return seguir();
        })
        .catch(() => liberar());
      return;
    }

    seguir();
  });

  document.addEventListener('shopify:cart:error', desarmar);
  document.addEventListener('cart:error', desarmar);
})();
